import { styled } from '@/styles/stitches.config';
import { countryIdToName } from '@/utils/country-id-to-name';
import { MouseEventHandler, useCallback } from 'react';
import { useTooltipContext } from './tooltipContext';
import { CountryData } from '@/pages/about';

const Anchor = styled('a', {
  cursor: 'pointer',
  '&:focus': {
    outline: 'none',
  },
});

const TouchTarget = styled('circle', {
  fill: 'transparent',
});

const Circle = styled('circle', {
  pointerEvents: 'none',
  fill: '$white',
  fillOpacity: 0.4,

  variants: {
    isActive: {
      true: {
        fill: '$white',
      },
    },
    isCenterTile: {
      true: {
        fillOpacity: 1,
        filter: 'drop-shadow(0px 0px 0.3px rgba(0, 0, 0, 0.50))',
        fill: '$unicef',
        stroke: '$white',
        strokeWidth: '0.3',

        'a:hover &, a:focus &': {
          fill: 'white',
        },
      },
    },
  },
});

const FocusRing = styled('circle', {
  stroke: 'none',
  strokeWidth: '0.3',
  fill: 'none',
  transition: 'stroke 0.1s linear',
  filter: 'drop-shadow(0px 0px 0.3px rgba(0, 0, 0, 0.50))',

  'a:hover &': {
    stroke: '$white',
  },

  variants: {
    isActive: {
      true: {
        stroke: '$white',
        strokeWidth: '0.4',
        strokeDasharray: '0.16 1',
        strokeLinecap: 'round',
      },
    },
  },
});

type PointProps = {
  countryData: CountryData;
  isCenterTile?: boolean;
  radius: number;
};

export function Point({ countryData, radius, isCenterTile }: PointProps) {
  const [x, y] = countryData.pos;

  const {
    getId,
    tooltipState,
    isPointActive,
    showTooltip,
    openTooltip,
    hideTooltip,
    closeTooltip,
  } = useTooltipContext();

  if (isCenterTile) {
    return (
      <Anchor
        key={getId(countryData)}
        href={`#${getId(countryData)}`}
        onFocus={(e) => openTooltip({ data: countryData, root: e.currentTarget })}
        onPointerDown={(e) => openTooltip({ data: countryData, root: e.currentTarget })}
        onPointerEnter={(e) => showTooltip({ data: countryData, root: e.currentTarget })}
        onPointerLeave={hideTooltip}
        onBlur={closeTooltip}
        aria-label={countryIdToName(getId(countryData))}
      >
        <TouchTarget r={radius * 2} cx={x} cy={y} />
        <Circle
          r={radius}
          cx={x}
          cy={y}
          isCenterTile
          isActive={tooltipState === 'OPEN' && isPointActive(countryData.id)}
        />
        <FocusRing
          r={radius * 2.2}
          cx={x}
          cy={y}
          isActive={tooltipState === 'OPEN' && isPointActive(countryData.id)}
        />
      </Anchor>
    );
  }

  return <Circle r={radius * 0.8} cx={x} cy={y} />;
}
